var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Emitter from './Util/Emitter';
import Constants from './Util/Constants';
import WsCommunication from './Tracker/WSCommunication';
import * as Peer from 'simple-peer';
import Util from './Util/Util';
import Log from '../../npawPlugin/src/common/log';
import { BalancerTag } from '../../npawPlugin/src/common/Constants';
import { messageMatchesProtocolVersion, messageMatchesSwarm, trackerActionRequiresRendition } from './Util/TrackerFilters';
const DEFAULT_OFFER_TIMEOUT_MS = 15000;
const MAX_OFFER_TIMEOUT_MS = 20000;
const MAX_OFFERS_PER_ANNOUNCE = 16;
const TRACKER_RECONNECT_DELAY = 5000;
const DEFAULT_PROTOCOL_VERSION = 2;
const MIN_OFFER_INTERVAL_MS = 5000;
/**
 * @class
 * @description Communication class that manages communication with a websocket tracker and gets peer candidates.
 * @extends Emitter
 * @exports P2PManager
 */
export default class P2PManager extends Emitter {
    /**
     * @constructs P2PManager
     * @param {string} hashId ID of the content as string, to be hashed.
     * @param {P2PConfig} config Configuration JSON, including the tracker URL, iceServers...
     * @param {string?} peerId Optional, ID of the peer/user, if not set a random one will be generated.
     * @public
     */
    constructor(loader, hashId, config, peerId) {
        super();
        this._peerList = new Map();
        this._sentOffers = new Map();
        this._myOffers = [];
        this._pendingCandidatesByOfferId = new Map();
        this._pendingCandidatesByPeerId = new Map();
        this.isOfferProcessStarted = false;
        this._destroyed = false;
        this._peerConnectionErrors = new Map();
        this._maxPeerErrorsConnectionToBan = 2;
        this._offerLoopIntervalMs = 20000;
        this._offerTimeoutMs = DEFAULT_OFFER_TIMEOUT_MS;
        this._discoveryStartedAt = 0;
        this._firstOfferReceived = false;
        this._loader = loader;
        this._config = config;
        this._hashId = Util.hash(hashId);
        this._config.protocolVersion = this._config.protocolVersion || DEFAULT_PROTOCOL_VERSION;
        this._peerId = peerId || Util.generateRandomPeerId();
        this._tracker = new WsCommunication(this._config);
        this._tracker
            .on(Constants.WSCommEvents.Data, this._dataReceived.bind(this))
            .on(Constants.WSCommEvents.Connected, () => {
            this.start();
        })
            .on(Constants.WSCommEvents.Closed, (tracker) => {
            if (!this._destroyed) {
                setTimeout(() => tracker.data.start(), TRACKER_RECONNECT_DELAY);
            }
        })
            .on(Constants.WSCommEvents.Error, (e) => {
            this.stop();
            Log.warn(BalancerTag, 'Socket error: ', e);
        })
            .start();
    }
    getId() {
        return this._peerId;
    }
    /**
     * Starts the communication with the websocket tracker
     * @public
     */
    start() {
        if (!this._loader.isEnabled()) {
            return;
        }
        this._discoveryStartedAt = Date.now();
        this._firstOfferReceived = false;
        this._sendWsMessage({ action: Constants.AnnounceEvents.Join });
    }
    /**
     * Ends the communication with the websocket tracker
     * @public
     */
    stop() {
        var _a;
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.close();
    }
    /**
     * Destroys the tracker reference and stops the communication
     * @public
     */
    destroy() {
        this._destroyed = true;
        this._clearPeerState();
        this.stop();
    }
    rejoin(hashId, switchSwarm = false) {
        var _a, _b;
        if (!this._loader.isEnabled()) {
            return;
        }
        this._hashId = Util.hash(hashId);
        this._discoveryStartedAt = Date.now();
        this._firstOfferReceived = false;
        if (switchSwarm) {
            this._clearPeerState();
            if (!((_a = this._tracker) === null || _a === void 0 ? void 0 : _a.isConnected())) {
                (_b = this._tracker) === null || _b === void 0 ? void 0 : _b.start();
                return;
            }
        }
        this._sendWsMessage({ action: Constants.AnnounceEvents.Join });
    }
    setRendition(rendition) {
        this._config.rendition = rendition;
    }
    setSwarmIdInput(input) {
        this._config.swarmGroup = input && input.length > 0 ? Util.hash(input) : undefined;
    }
    /**
     * L2: canonical swarm identity used in the tracker Join payload
     * (`canonical_swarm_id` + `canonical_swarm_source`). Android/iOS send this
     * when the P2PManifestRegistry resolves a SwarmIdentity, so the tracker
     * can surface lineage in the dashboard (HLS_MASTER / DASH_MPD / VIDEO_ID /
     * URL_FALLBACK). Both fields cleared when id/source are empty/undefined.
     */
    setCanonicalSwarmIdentity(canonicalId, source) {
        this._config.canonicalSwarmId = canonicalId && canonicalId.length > 0 ? canonicalId : undefined;
        this._config.canonicalSwarmSource = source && source.length > 0 ? source : undefined;
    }
    setResource(resource) {
        this._config.resource = resource;
    }
    setConfigName(configName) {
        this._config.configName = configName;
    }
    /**
     * Sends a message to the tracker if available
     * @param {trackerMessage} message JSON object with the data to be sent to the tracker.
     * @private
     */
    _sendWsMessage(message = {}) {
        var _a;
        if (this._destroyed)
            return;
        const action = message.action;
        // M2: rendition-gated actions are dropped entirely when no active rendition
        // is known. Tracker would reject them as malformed (parity with Android
        // `sendWsMessage`'s early return).
        if (action && this._requiresRendition(action)) {
            const rendition = this._config.rendition;
            if (!rendition || rendition.length === 0) {
                Log.verbose(BalancerTag, 'Skipping tracker message ' + action + ' because rendition is missing');
                return;
            }
        }
        const obj = {
            info_hash: this._hashId,
            peer_id: this._peerId,
            token: this._config.token || Constants.Token,
            account_code: this._config.accountCode,
            protocol_version: this._config.protocolVersion || DEFAULT_PROTOCOL_VERSION
        };
        const payload = Object.assign(obj, message);
        if (action && this._requiresRendition(action) && this._config.rendition) {
            payload.rendition = this._config.rendition;
        }
        if (action === Constants.AnnounceEvents.Join) {
            if (this._config.resource) {
                payload.resource = this._config.resource;
            }
            // L2: ship the canonical swarm identity when the registry has resolved one.
            if (this._config.canonicalSwarmId) {
                payload.canonical_swarm_id = this._config.canonicalSwarmId;
            }
            if (this._config.canonicalSwarmSource) {
                payload.canonical_swarm_source = this._config.canonicalSwarmSource;
            }
        }
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.send(payload);
    }
    _requiresRendition(action) {
        return trackerActionRequiresRendition(action);
    }
    _isMessageCompatible(data) {
        const expectedVersion = this._config.protocolVersion || DEFAULT_PROTOCOL_VERSION;
        const ok = messageMatchesProtocolVersion(data, expectedVersion);
        if (!ok) {
            Log.verbose(BalancerTag, 'Ignoring tracker message for protocol_version=' + (data === null || data === void 0 ? void 0 : data.protocol_version) + ', active=' + expectedVersion);
        }
        return ok;
    }
    _isMessageForCurrentSwarm(data) {
        const ok = messageMatchesSwarm(data, this._config.rendition, this._hashId);
        if (!ok) {
            Log.verbose(BalancerTag, 'Ignoring tracker message for stale swarm context — rendition=' +
                (data === null || data === void 0 ? void 0 : data.rendition) +
                ', info_hash=' +
                (data === null || data === void 0 ? void 0 : data.info_hash) +
                ', active rendition=' +
                this._config.rendition +
                ', active hash=' +
                this._hashId);
        }
        return ok;
    }
    _clearPeerState() {
        this._pendingCandidatesByOfferId.clear();
        this._pendingCandidatesByPeerId.clear();
        for (const [, peer] of this._sentOffers) {
            try {
                peer.destroy();
            }
            catch (_) {
                Log.verbose(BalancerTag, 'Ignoring peer destroy error while clearing sent offers');
            }
        }
        this._sentOffers.clear();
        for (const [, peer] of this._peerList) {
            try {
                peer.destroy();
            }
            catch (_) {
                Log.verbose(BalancerTag, 'Ignoring peer destroy error while clearing peer list');
            }
        }
        this._peerList.clear();
    }
    /**
     * Generates an array of offers to be used/sent to the tracker so we can get answers to connect to peers
     * @private
     */
    _generateAndSendOfferList() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._destroyed)
                return;
            if (this.isOfferProcessStarted)
                return;
            this.isOfferProcessStarted = true;
            while (true && !this._destroyed) {
                this._myOffers = [];
                if (!this._loader.isEnabled()) {
                    yield new Promise((f) => setTimeout(f, this._offerLoopIntervalMs));
                    continue;
                }
                if (!this._loader.isMaxPeers() && this._loader.getStorage().getSize() > 0) {
                    const announcePeers = this._config.announcePeers || Constants.Default.AnnouncePeers;
                    const total = Math.max(0, Math.min(announcePeers, MAX_OFFERS_PER_ANNOUNCE));
                    for (let i = 0; i < total; ++i) {
                        const offer_id = Util.generateRandomPeerId();
                        const peer = this._createPeer(offer_id);
                        if (!peer)
                            continue;
                        peer.once(Constants.PeerEvents.Signal, (offer) => {
                            this._myOffers.push({
                                offer_id: offer_id,
                                sdp: offer.sdp,
                                type: offer.type
                            });
                            this._sentOffers.set(offer_id, peer);
                        });
                        setTimeout(() => {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            if (peer.id === offer_id) {
                                //  destroying offer after timeout, if peer id still is offer_id
                                this._sentOffers.delete(offer_id);
                                this._pendingCandidatesByOfferId.delete(offer_id);
                                peer.destroy();
                            }
                        }, this._offerTimeoutMs);
                    }
                    const init = Date.now();
                    //console.log("PA1 a");
                    while (this._myOffers.length < total &&
                        Date.now() - init < 10000) {
                        yield new Promise((f) => setTimeout(f, 50));
                    }
                    //console.log("PA1 b");
                    if (this._myOffers.length > 0) {
                        Log.debug(BalancerTag, 'SENDING OFFERS ' + this._myOffers.length);
                        this._sendWsMessage({ action: Constants.AnnounceEvents.Offers, offers: this._myOffers });
                    }
                }
                // console.log("PAX1 -----> once "+this._myOffers.length);
                yield new Promise((f) => setTimeout(f, this._offerLoopIntervalMs));
            }
        });
    }
    /**
     * Creates a peer to offer it, or to send it as answer.
     * @param {string} id ID of the peer to be created.
     * @param {boolean} initiator By defult true, if it has to generate the offer. If not generates the answer.
     * @returns {Peer.Instance} Peer object waiting for events/responses
     * @private
     */
    _createPeer(id, initiator = true) {
        const peer = new Peer({
            initiator,
            trickle: false,
            config: this._config.iceServers || Constants.iceServers
            //wrtc: undefined
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        peer.id = id;
        if (this._destroyed) {
            peer.destroy();
            return undefined;
        }
        if (!this._loader.isEnabled() || this._loader.getBannedPeers().get(id) || this._loader.isMaxPeers()) {
            peer.destroy();
            return undefined;
        }
        const connect = () => {
            peer.off(Constants.PeerEvents.Connect, connect);
            peer.off(Constants.PeerEvents.Error, error);
        };
        const error = (err) => {
            this.emit(Constants.WSCommEvents.Warning, 'Error with the peer id: ' + peer.id + ' ' + err);
            let cont = this._peerConnectionErrors.get(peer.id);
            if (cont) {
                if (cont >= this._maxPeerErrorsConnectionToBan) {
                    this._loader.getBannedPeers().set(peer.id, true);
                    Log.debug(BalancerTag, 'WARN Banning peer ' + peer.id + ' errors: ' + cont);
                }
                else {
                    cont++;
                    this._peerConnectionErrors.set(peer.id, cont);
                }
            }
            else {
                this._peerConnectionErrors.set(peer.id, 1);
            }
            //console.log("PA1 Peer error");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this._peerList.delete(peer.id);
            this._sentOffers.delete(peer.id);
            this._pendingCandidatesByOfferId.delete(peer.id);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this._pendingCandidatesByPeerId.delete(peer.id);
            peer.destroy();
        };
        const close = () => {
            //console.log("PA1 Clossing peer");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this._peerList.delete(peer.id);
            this._sentOffers.delete(peer.id);
            this._pendingCandidatesByOfferId.delete(peer.id);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this._pendingCandidatesByPeerId.delete(peer.id);
            peer.destroy();
        };
        peer.on(Constants.PeerEvents.Connect, connect);
        peer.on(Constants.PeerEvents.Error, error);
        peer.on(Constants.PeerEvents.Close, close);
        return peer;
    }
    /**
     * Callback for data events from the tracker
     * It manages all the events used: Offer, Candidates, Answer, Join
     * @param {callbackData} e event callback data
     * @private
     */
    _dataReceived(e) {
        if (this._destroyed)
            return;
        const data = e.data;
        if (!data || typeof data.action !== 'string') {
            return;
        }
        if (!this._isMessageCompatible(data)) {
            return;
        }
        if (!this._isMessageForCurrentSwarm(data)) {
            return;
        }
        switch (data.action) {
            case Constants.AnnounceEvents.Offer:
                this._handleOffer(data);
                break;
            case Constants.AnnounceEvents.Candidates:
                this._handleCandidates(data);
                break;
            case Constants.AnnounceEvents.Answer:
                this._handleAnswer(data);
                break;
            case Constants.AnnounceEvents.Join:
                this._handleJoin(data);
                break;
            case Constants.AnnounceEvents.Config:
                this._handleConfig(data);
                break;
        }
    }
    /**
     * Handles tracker join event
     * @param {joinResponse} e event callback data
     * @private
     */
    /*private _handleJoin(e: joinResponse) {
      if ('ok' === e.status) {
        setTimeout(() => {
          this._sendWsMessage({ action: Constants.AnnounceEvents.Offers, offers: this._myOffers });
        }, Constants.trackerCommunicationWait+6000);
        setInterval(
          () => {
            this._generateOfferList();
            setTimeout(() => {
              this._sendWsMessage({ action: Constants.AnnounceEvents.Offers, offers: this._myOffers });
            }, Constants.trackerCommunicationWait + 5000);
          },
          e.interval ? e.interval * 1000 : Constants.trackerInterval
        );
      } else {
        // e.status === 'error'
        setTimeout(this.start, Constants.trackerRetryJoinTime);
      }
    }*/
    _handleJoin(e) {
        Log.verbose(BalancerTag, 'Handling P2P join');
        if (this._destroyed)
            return;
        if ('ok' === e.status) {
            if (typeof e.interval === 'number' && e.interval > 0) {
                this._offerLoopIntervalMs = Math.max(e.interval * 1000, MIN_OFFER_INTERVAL_MS);
            }
            if (typeof e.offer_timeout === 'number' && e.offer_timeout > 0) {
                this._offerTimeoutMs = Math.min(e.offer_timeout * 1000, MAX_OFFER_TIMEOUT_MS);
            }
            else {
                this._offerTimeoutMs = DEFAULT_OFFER_TIMEOUT_MS;
            }
            if (typeof e.p2p_enabled === 'boolean') {
                this._loader.setRuntimeP2pEnabled(e.p2p_enabled);
            }
            this._generateAndSendOfferList();
        }
        else {
            setTimeout(() => this.start(), Constants.trackerRetryJoinTime);
        }
    }
    _handleConfig(e) {
        if (typeof e.p2p_enabled !== 'boolean') {
            return;
        }
        this._loader.setRuntimeP2pEnabled(e.p2p_enabled);
    }
    /**
     * Handles tracker answer event
     * @param {answerData} e event callback data
     * @private
     */
    _handleAnswer(e) {
        Log.verbose(BalancerTag, 'Handling P2P answer ' + e.peer_id);
        if (this._destroyed)
            return;
        const offerId = e.answer.offer_id;
        const peer = this._sentOffers.get(offerId);
        if (!this._loader.isEnabled() || this._loader.getBannedPeers().get(e.peer_id)) {
            if (peer) {
                this._sentOffers.delete(offerId);
                try {
                    peer.destroy();
                }
                catch (_) {
                    // ignore
                }
            }
            this._pendingCandidatesByOfferId.delete(offerId);
            return;
        }
        if (!peer) {
            Log.verbose(BalancerTag, 'Failed to find peer for offer_id ' + offerId);
            this._pendingCandidatesByOfferId.delete(offerId);
            return;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        peer.id = e.peer_id;
        const existingPeer = this._peerList.get(e.peer_id);
        if (existingPeer && existingPeer !== peer) {
            if (this._shouldKeepExistingPeer(e.peer_id, existingPeer)) {
                Log.verbose(BalancerTag, 'Discarding answered offer_id ' +
                    offerId +
                    ' for peer_id ' +
                    e.peer_id +
                    ' — existing peer won glare resolution');
                this._sentOffers.delete(offerId);
                this._pendingCandidatesByOfferId.delete(offerId);
                try {
                    peer.destroy();
                }
                catch (_) {
                    // ignore
                }
                return;
            }
            Log.verbose(BalancerTag, 'Replacing existing peer for peer_id ' +
                e.peer_id +
                ' with answered offer_id ' +
                offerId +
                ' after glare resolution');
            try {
                existingPeer.destroy();
            }
            catch (_) {
                // ignore
            }
            this._peerList.delete(e.peer_id);
        }
        this._sentOffers.delete(offerId);
        this._peerList.set(e.peer_id, peer);
        this._loader.addPeerCandidate(peer, e.answer);
        peer.signal(e.answer);
        this._drainPendingCandidates(peer, e.peer_id, offerId);
    }
    /**
     * Handles tracker candidates event
     * @param {candidatesData} e event callback data
     * @private
     */
    _handleCandidates(e) {
        var _a;
        if (this._destroyed)
            return;
        if (!this._loader.isEnabled() || this._loader.getBannedPeers().get(e.peer_id)) {
            this._pendingCandidatesByPeerId.delete(e.peer_id);
            if (e.offer_id) {
                const pendingOfferPeer = this._sentOffers.get(e.offer_id);
                this._sentOffers.delete(e.offer_id);
                this._pendingCandidatesByOfferId.delete(e.offer_id);
                if (pendingOfferPeer) {
                    try {
                        pendingOfferPeer.destroy();
                    }
                    catch (_) {
                        // ignore
                    }
                }
            }
            return;
        }
        let candidates = (_a = e.candidates) !== null && _a !== void 0 ? _a : [];
        if (this._loader.getMissingPeers() > 0) {
            candidates = candidates.slice(0, this._loader.getMissingPeers());
        }
        if (candidates.length === 0)
            return;
        let peer = e.offer_id ? this._sentOffers.get(e.offer_id) : undefined;
        if (!peer) {
            peer = this._peerList.get(e.peer_id);
        }
        if (peer) {
            this._applyIceCandidates(peer, candidates);
            return;
        }
        if (!e.offer_id) {
            Log.verbose(BalancerTag, 'Dropping candidates for unknown peer_id ' + e.peer_id + ' (no offer_id)');
            return;
        }
        this._bufferPendingCandidates(e.peer_id, e.offer_id, candidates);
        Log.verbose(BalancerTag, 'Buffered ' + candidates.length + ' ICE candidates for peer_id ' + e.peer_id + ' offer_id ' + e.offer_id);
    }
    _applyIceCandidates(peer, candidates) {
        for (const c of candidates) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                peer.signal({ candidate: { candidate: c.candidate, sdpMLineIndex: 0, sdpMid: '0' } });
            }
            catch (err) {
                Log.verbose(BalancerTag, 'Failed to apply ICE candidate: ' + err);
            }
        }
    }
    _bufferPendingCandidates(peerId, offerId, candidates) {
        if (candidates.length === 0)
            return;
        const targetMap = offerId ? this._pendingCandidatesByOfferId : this._pendingCandidatesByPeerId;
        const key = offerId !== null && offerId !== void 0 ? offerId : peerId;
        const existing = targetMap.get(key);
        if (existing) {
            existing.push(...candidates);
        }
        else {
            targetMap.set(key, [...candidates]);
        }
    }
    _drainPendingCandidates(peer, peerId, offerId) {
        const drained = [];
        if (offerId) {
            const byOffer = this._pendingCandidatesByOfferId.get(offerId);
            if (byOffer) {
                drained.push(...byOffer);
                this._pendingCandidatesByOfferId.delete(offerId);
            }
        }
        if (peerId) {
            const byPeer = this._pendingCandidatesByPeerId.get(peerId);
            if (byPeer) {
                drained.push(...byPeer);
                this._pendingCandidatesByPeerId.delete(peerId);
            }
        }
        if (drained.length > 0) {
            Log.verbose(BalancerTag, 'Draining ' + drained.length + ' pending ICE candidates for peer_id=' + peerId + ' offer_id=' + offerId);
            this._applyIceCandidates(peer, drained);
        }
    }
    _shouldKeepExistingPeer(remotePeerId, existingPeer) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (existingPeer.connected) {
            return true;
        }
        const localPeerId = this._peerId;
        if (!localPeerId) {
            return true;
        }
        return localPeerId > remotePeerId;
    }
    /**
     * Handles tracker offer event
     * @param {offerData} e event callback data
     * @private
     */
    _handleOffer(e) {
        if (this._destroyed)
            return;
        if (!this._loader.isEnabled())
            return;
        const peerId = e.peer_id;
        if (this._loader.getBannedPeers().get(peerId) != undefined || this._loader.isMaxPeers()) {
            return;
        }
        const offer = e.offer;
        const offerId = offer.offer_id;
        if (this._peerList.has(peerId)) {
            // peerId already connected
            return;
        }
        Log.verbose(BalancerTag, 'Handling P2P offer ' + peerId);
        this._loader.onOffer(peerId, this._offerLoopIntervalMs);
        if (!this._firstOfferReceived && this._discoveryStartedAt > 0) {
            this._firstOfferReceived = true;
            this._loader.onDiscovery(Date.now() - this._discoveryStartedAt);
        }
        const peer = this._createPeer(peerId, false);
        if (!peer)
            return;
        peer.once(Constants.PeerEvents.Signal, (e) => {
            const answer = {
                offer_id: offerId,
                sdp: e.sdp,
                type: e.type
            };
            this._sendWsMessage({
                action: Constants.AnnounceEvents.Answer,
                to_peer_id: peerId,
                answer
            });
        });
        //this.emit(Constants.WebRtcEvents.Peer, peer);
        this._loader.addPeerCandidate(peer, undefined);
        peer.signal(offer);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._peerList.set(peer.id, peer);
    }
    static parseCandidate(line) {
        let parts;
        // Parse both variants.
        if (line.indexOf('a=candidate:') === 0) {
            parts = line.substring(12).split(' ');
        }
        else {
            parts = line.substring(10).split(' ');
        }
        const candidate = {
            foundation: parts[0],
            component: parts[1],
            protocol: parts[2].toLowerCase(),
            priority: parseInt(parts[3], 10),
            ip: parts[4],
            port: parseInt(parts[5], 10),
            // skip parts[6] == 'typ'
            type: parts[7],
            relatedAddress: '',
            relatedPort: 0,
            tcpType: ''
        };
        for (let i = 8; i < parts.length; i += 2) {
            switch (parts[i]) {
                case 'raddr':
                    candidate.relatedAddress = parts[i + 1];
                    break;
                case 'rport':
                    candidate.relatedPort = parseInt(parts[i + 1], 10);
                    break;
                case 'tcptype':
                    candidate.tcpType = parts[i + 1];
                    break;
                default: // Unknown extensions are silently ignored.
                    break;
            }
        }
        return candidate;
    }
    test() {
        const candidates = {};
        const pc = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun3.l.google.com:19302' }, { urls: 'stun:stun4.l.google.com:19302' }]
        });
        pc.createDataChannel('testchannel');
        pc.onicecandidate = function (e) {
            if (e.candidate && e.candidate.candidate.indexOf('srflx') !== -1) {
                const cand = P2PManager.parseCandidate(e.candidate.candidate);
                if (!candidates[cand.relatedPort])
                    candidates[cand.relatedPort] = [];
                candidates[cand.relatedPort].push(cand.port);
            }
            else if (!e.candidate) {
                if (Object.keys(candidates).length === 1) {
                    const ports = candidates[Object.keys(candidates)[0]];
                    Log.debug(BalancerTag, ports.length === 1 ? 'normal nattt' : 'symmetric nattt');
                }
            }
        };
    }
}
P2PManager.Util = Util;
P2PManager.Constants = Constants;
window.P2PManager = P2PManager;
